import type { ChessColor } from "../chess/rules";
import type { EvaluationScore } from "../chess/evaluation";
import type { TimeControl } from "../types/player";

export const MAX_REVIEW_NOTE_TEXT_LENGTH = 2_000;
export const MAX_REVIEW_NOTE_CANDIDATES = 12;
export const MAX_REVIEW_NOTE_CANDIDATE_LENGTH = 32;

export type ReviewSubjectiveEvaluation = "better" | "equal" | "worse" | "unclear";

export interface ReviewPlyNoteInput {
  intention?: string | null;
  candidateMoves?: readonly string[];
  calculatedLine?: string | null;
  expectedReply?: string | null;
  subjectiveEvaluation?: ReviewSubjectiveEvaluation | null;
  outOfBook?: boolean;
  reviewIdea?: string | null;
}

export interface ReviewPlyNote {
  gameId: string;
  ply: number;
  intention: string | null;
  candidateMoves: readonly string[];
  calculatedLine: string | null;
  expectedReply: string | null;
  subjectiveEvaluation: ReviewSubjectiveEvaluation | null;
  outOfBook: boolean;
  reviewIdea: string | null;
  createdAt: string;
  updatedAt: string;
}

const reviewNoteTextFields = ["intention", "calculatedLine", "expectedReply", "reviewIdea"] as const;
const subjectiveEvaluations = new Set<ReviewSubjectiveEvaluation>(["better", "equal", "worse", "unclear"]);

/** Validates untrusted note input and trims only its outer whitespace. */
export function parseReviewPlyNoteInput(value: unknown): ReviewPlyNoteInput | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return null;
  const source = value as Record<string, unknown>;
  const parsed: ReviewPlyNoteInput = {};

  for (const field of reviewNoteTextFields) {
    const fieldValue = source[field];
    if (fieldValue === undefined) continue;
    if (fieldValue === null) {
      parsed[field] = null;
      continue;
    }
    if (typeof fieldValue !== "string" || [...fieldValue].length > MAX_REVIEW_NOTE_TEXT_LENGTH) return null;
    parsed[field] = fieldValue.trim();
  }

  if (source.candidateMoves !== undefined) {
    if (!Array.isArray(source.candidateMoves) || source.candidateMoves.length > MAX_REVIEW_NOTE_CANDIDATES) return null;
    const candidates: string[] = [];
    for (const candidate of source.candidateMoves) {
      if (typeof candidate !== "string" || [...candidate].length > MAX_REVIEW_NOTE_CANDIDATE_LENGTH) return null;
      const trimmed = candidate.trim();
      if (trimmed) candidates.push(trimmed);
    }
    parsed.candidateMoves = candidates;
  }

  if (source.subjectiveEvaluation !== undefined) {
    if (
      source.subjectiveEvaluation !== null &&
      (typeof source.subjectiveEvaluation !== "string" ||
        !subjectiveEvaluations.has(source.subjectiveEvaluation as ReviewSubjectiveEvaluation))
    ) {
      return null;
    }
    parsed.subjectiveEvaluation = source.subjectiveEvaluation as ReviewSubjectiveEvaluation | null;
  }

  if (source.outOfBook !== undefined) {
    if (typeof source.outOfBook !== "boolean") return null;
    parsed.outOfBook = source.outOfBook;
  }

  return parsed;
}

export type CriticalMomentKind =
  "player_error" | "opponent_miss" | "missed_opportunity" | "turning_point" | "conversion";

export type NikolaActivityKind =
  | "coach_thinking"
  | "commentary"
  | "reasoning_summary"
  | "player_data_checked"
  | "games_reviewed"
  | "position_checked"
  | "line_compared"
  | "patterns_checked"
  | "development_checked"
  | "guide_checked"
  | "analysis_started"
  | "memory_searched"
  | "web_searched"
  | "source_read"
  | "sources_cross_checked";

export type NikolaActivityIcon =
  | "sync_games"
  | "recent_games"
  | "start_analysis"
  | "refresh_analysis"
  | "game_review"
  | "game_search"
  | "player_record"
  | "player_development"
  | "pattern_record"
  | "line_evaluation"
  | "move_evidence"
  | "critical_moments"
  | "pattern_examples"
  | "candidate_comparison"
  | "help_guide"
  | "memory_search"
  | "web_search"
  | "source_read"
  | "source_cross_check";

export interface NikolaActivity {
  activityId?: string;
  sequence?: number;
  kind: NikolaActivityKind;
  status: "running" | "complete" | "failed";
  label: string;
  icon?: NikolaActivityIcon;
  detail?: string;
  occurredAt?: string;
  /** Determinate browser-owned progress, from 0 through 100. Omitted for provider/tool work with unknown duration. */
  progress?: number;
}

export interface NikolaEvidenceCard {
  id: string;
  title: string;
  summary: string;
  supportingFactIds: readonly string[];
  gameId?: string;
  ply?: number;
  fen?: string;
  reference?: NikolaGameReference;
}

export interface NikolaGameReference {
  gameId: string;
  factId?: string;
  ply?: number;
  moveNumber?: number;
  san?: string;
  fen?: string;
  opponent?: string;
  playedAt?: string;
  timeControl?: TimeControl;
  result?: string;
}

export type NikolaSourceCitation =
  | {
      kind: "memory" | "chessla";
      label: string;
      sessionId?: string;
      game?: NikolaGameReference;
    }
  | {
      kind: "official_web" | "web";
      label: string;
      url: string;
      domain: string;
      summary?: string;
      publishedAt?: string;
      retrievedAt: string;
    };

export interface NikolaAction {
  type:
    | "start_game_analysis"
    | "evaluate_line"
    | "open_game"
    | "open_pattern"
    | "save_practice_set"
    | "download_export"
    | "open_nikola_settings";
  label: string;
  gameId?: string;
  patternId?: string;
  fen?: string;
  maxPlies?: number;
  draftId?: string;
  exportId?: string;
}

export interface NikolaCoachingReason {
  text: string;
  evidenceIds: readonly string[];
}

export interface NikolaCoachingLine {
  label: string;
  san: readonly string[];
  evidenceIds: readonly string[];
}

export interface NikolaCoachingAnswer {
  verdict: string;
  contrast?: string;
  line?: NikolaCoachingLine;
  reasons: readonly NikolaCoachingReason[];
  principle?: string;
}

export type NikolaMessagePart =
  | { type: "text"; markdown: string }
  | { type: "activity"; activity: NikolaActivity }
  | { type: "evidence"; evidence: NikolaEvidenceCard }
  | { type: "actions"; actions: readonly NikolaAction[] }
  | { type: "board"; fen: string; orientation: ChessColor; supportingFactIds: readonly string[] }
  | { type: "sources"; sources: readonly NikolaSourceCitation[] }
  | { type: "coaching"; coaching: NikolaCoachingAnswer };

export const MAX_NIKOLA_MESSAGE_PARTS = 20;
export const MAX_NIKOLA_MESSAGE_METADATA_BYTES = 32_768;
export const MAX_NIKOLA_COACHING_TEXT_LENGTH = 600;
export const MAX_NIKOLA_COACHING_REASONS = 3;
export const MAX_NIKOLA_COACHING_LINE_PLIES = 8;
export const MAX_NIKOLA_COACHING_EVIDENCE_IDS = 8;
export const MAX_NIKOLA_SOURCES = 8;
export const MAX_REVIEW_REFLECTION_LENGTH = 2_000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isDenseArray = (value: readonly unknown[]): boolean => {
  for (let index = 0; index < value.length; index += 1) if (!(index in value)) return false;
  return true;
};
const isStringArray = (value: unknown): value is readonly string[] =>
  Array.isArray(value) && isDenseArray(value) && value.every((item) => typeof item === "string");
const hasOnlyKeys = (value: Record<string, unknown>, keys: readonly string[]): boolean =>
  Object.keys(value).every((key) => keys.includes(key));
const optionalString = (value: unknown): value is string | undefined =>
  value === undefined || typeof value === "string";
const boundedOptionalString = (value: unknown, max: number): value is string | undefined =>
  value === undefined || (typeof value === "string" && value.length > 0 && value.length <= max);
const boundedOptionalInteger = (value: unknown, max: number): value is number | undefined =>
  value === undefined || (typeof value === "number" && Number.isInteger(value) && value > 0 && value <= max);
const timeControls = new Set<TimeControl>(["bullet", "blitz", "rapid", "daily"]);
const SAN = /^(?:O-O(?:-O)?|[KQRBN]?[a-h]?[1-8]?x?[a-h][1-8](?:=[QRBN])?)[+#]?$/;

const normalizeCoachingText = (value: unknown): string | null => {
  if (typeof value !== "string" || [...value].length > MAX_NIKOLA_COACHING_TEXT_LENGTH) return null;
  const text = value.trim();
  return text ? text : null;
};

const normalizeEvidenceIds = (value: unknown): string[] | null => {
  if (
    !Array.isArray(value) ||
    !isDenseArray(value) ||
    value.length === 0 ||
    value.length > MAX_NIKOLA_COACHING_EVIDENCE_IDS
  )
    return null;
  const ids = value.map((item) => (typeof item === "string" ? item.trim() : ""));
  return ids.every((id) => id.length > 0 && [...id].length <= 512) && new Set(ids).size === ids.length ? ids : null;
};

export const normalizeNikolaCoachingAnswer = (value: unknown): NikolaCoachingAnswer | null => {
  if (!isRecord(value) || !hasOnlyKeys(value, ["verdict", "contrast", "line", "reasons", "principle"])) return null;
  const verdict = normalizeCoachingText(value.verdict);
  const contrast = value.contrast === undefined ? undefined : normalizeCoachingText(value.contrast);
  const principle = value.principle === undefined ? undefined : normalizeCoachingText(value.principle);
  if (!verdict || contrast === null || principle === null) return null;
  if (
    !Array.isArray(value.reasons) ||
    !isDenseArray(value.reasons) ||
    value.reasons.length === 0 ||
    value.reasons.length > MAX_NIKOLA_COACHING_REASONS
  )
    return null;

  const usedEvidenceIds = new Set<string>();
  const takeEvidenceIds = (candidate: unknown): string[] | null => {
    const ids = normalizeEvidenceIds(candidate);
    if (!ids || ids.some((id) => usedEvidenceIds.has(id))) return null;
    ids.forEach((id) => usedEvidenceIds.add(id));
    return ids;
  };
  const reasons: NikolaCoachingReason[] = [];
  for (const candidate of value.reasons) {
    if (!isRecord(candidate) || !hasOnlyKeys(candidate, ["text", "evidenceIds"])) return null;
    const text = normalizeCoachingText(candidate.text);
    const evidenceIds = takeEvidenceIds(candidate.evidenceIds);
    if (!text || !evidenceIds) return null;
    reasons.push({ text, evidenceIds });
  }

  let line: NikolaCoachingLine | undefined;
  if (value.line !== undefined) {
    if (!isRecord(value.line) || !hasOnlyKeys(value.line, ["label", "san", "evidenceIds"])) return null;
    const label = normalizeCoachingText(value.line.label);
    const evidenceIds = takeEvidenceIds(value.line.evidenceIds);
    if (
      !label ||
      !evidenceIds ||
      !Array.isArray(value.line.san) ||
      !isDenseArray(value.line.san) ||
      value.line.san.length === 0 ||
      value.line.san.length > MAX_NIKOLA_COACHING_LINE_PLIES ||
      !value.line.san.every((move) => typeof move === "string" && move.length <= 32 && SAN.test(move))
    )
      return null;
    line = { label, san: value.line.san, evidenceIds };
  }

  return {
    verdict,
    ...(contrast === undefined ? {} : { contrast }),
    ...(line ? { line } : {}),
    reasons,
    ...(principle === undefined ? {} : { principle }),
  };
};

const normalizeNikolaGameReference = (value: unknown): NikolaGameReference | null => {
  if (!isRecord(value) || !boundedOptionalString(value.gameId, 256) || value.gameId === undefined) return null;
  if (
    !boundedOptionalString(value.factId, 512) ||
    !boundedOptionalInteger(value.ply, 800) ||
    !boundedOptionalInteger(value.moveNumber, 400)
  )
    return null;
  if (
    !boundedOptionalString(value.san, 32) ||
    !boundedOptionalString(value.fen, 160) ||
    !boundedOptionalString(value.opponent, 64)
  )
    return null;
  if (!boundedOptionalString(value.playedAt, 40) || !boundedOptionalString(value.result, 16)) return null;
  if (
    value.timeControl !== undefined &&
    (typeof value.timeControl !== "string" || !timeControls.has(value.timeControl as TimeControl))
  )
    return null;
  return {
    gameId: value.gameId,
    ...(value.factId === undefined ? {} : { factId: value.factId }),
    ...(value.ply === undefined ? {} : { ply: value.ply }),
    ...(value.moveNumber === undefined ? {} : { moveNumber: value.moveNumber }),
    ...(value.san === undefined ? {} : { san: value.san }),
    ...(value.fen === undefined ? {} : { fen: value.fen }),
    ...(value.opponent === undefined ? {} : { opponent: value.opponent }),
    ...(value.playedAt === undefined ? {} : { playedAt: value.playedAt }),
    ...(value.timeControl === undefined ? {} : { timeControl: value.timeControl as TimeControl }),
    ...(value.result === undefined ? {} : { result: value.result }),
  };
};

const boundedDate = (value: unknown): value is string => {
  if (typeof value !== "string" || value.length > 40) return false;
  const shaped = /^\d{4}-\d{2}-\d{2}$/u.test(value) || (value.length >= 20 && value[10] === "T" && value.endsWith("Z"));
  return shaped && !Number.isNaN(Date.parse(value));
};

const privateCitationText =
  /\b(?:anthropic|bedrock|claude|gpt|kimi|moonshot|openai|qwen|signature|stockfish|internal)\b|\b[a-z]+_[a-z_]+\b|[{}]|\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/iu;
const privateActivityText =
  /\b(?:anthropic|bedrock|claude|gpt|kimi|model|moonshot|openai|provider|qwen|signature|stockfish|engine|internal|tool)\b|\b[a-z]+_[a-z_]+\b|[{}]|\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/iu;
const privateActivityId = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/iu;

export const normalizeNikolaSourceCitation = (value: unknown): NikolaSourceCitation | null => {
  if (!isRecord(value) || typeof value.label !== "string" || !value.label.trim() || value.label.length > 120)
    return null;
  if (privateCitationText.test(value.label)) return null;
  if (value.kind === "memory" || value.kind === "chessla") {
    if (!hasOnlyKeys(value, ["kind", "label", "sessionId", "game"])) return null;
    if (!boundedOptionalString(value.sessionId, 256)) return null;
    const game = value.game === undefined ? undefined : normalizeNikolaGameReference(value.game);
    if (game === null || (value.kind === "memory" && value.sessionId === undefined && game === undefined)) return null;
    return {
      kind: value.kind,
      label: value.label.trim(),
      ...(value.sessionId === undefined ? {} : { sessionId: value.sessionId }),
      ...(game === undefined ? {} : { game }),
    };
  }
  if (value.kind !== "official_web" && value.kind !== "web") return null;
  if (!hasOnlyKeys(value, ["kind", "label", "url", "domain", "summary", "publishedAt", "retrievedAt"])) return null;
  if (
    typeof value.url !== "string" ||
    typeof value.domain !== "string" ||
    !value.domain ||
    value.domain.length > 253 ||
    !boundedOptionalString(value.summary, 800) ||
    !boundedDate(value.retrievedAt) ||
    (value.publishedAt !== undefined && !boundedDate(value.publishedAt))
  )
    return null;
  const summary = value.summary?.trim();
  const authority = value.url.startsWith("https://") ? value.url.slice(8).split(/[/?#]/u)[0] : undefined;
  const [hostname, port, extra] = authority?.split(":") ?? [];
  if (
    !hostname ||
    extra !== undefined ||
    (port !== undefined && !/^\d{1,5}$/u.test(port)) ||
    !/^[a-z0-9.-]{1,253}$/iu.test(hostname) ||
    value.url.length > 2_048 ||
    /\s/u.test(value.url) ||
    hostname.toLowerCase() !== value.domain.toLowerCase()
  )
    return null;
  return {
    kind: value.kind,
    label: value.label.trim(),
    url: value.url,
    domain: value.domain.toLowerCase(),
    ...(summary && !privateCitationText.test(summary) ? { summary } : {}),
    ...(value.publishedAt === undefined ? {} : { publishedAt: value.publishedAt }),
    retrievedAt: value.retrievedAt,
  };
};
const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (
      code >= 0xd800 &&
      code <= 0xdbff &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const normalizeNikolaAction = (value: unknown): NikolaAction | null => {
  if (
    !isRecord(value) ||
    typeof value.label !== "string" ||
    !optionalString(value.gameId) ||
    !optionalString(value.patternId) ||
    !optionalString(value.fen) ||
    !optionalString(value.draftId) ||
    !optionalString(value.exportId) ||
    !boundedOptionalInteger(value.maxPlies, 16)
  )
    return null;
  if (
    value.type !== "start_game_analysis" &&
    value.type !== "evaluate_line" &&
    value.type !== "open_game" &&
    value.type !== "open_pattern" &&
    value.type !== "save_practice_set" &&
    value.type !== "download_export" &&
    value.type !== "open_nikola_settings"
  )
    return null;
  if (value.type === "save_practice_set" && !value.draftId) return null;
  if (value.type === "download_export" && !value.exportId) return null;
  return {
    type: value.type,
    label: value.label,
    ...(value.gameId === undefined ? {} : { gameId: value.gameId }),
    ...(value.patternId === undefined ? {} : { patternId: value.patternId }),
    ...(value.fen === undefined ? {} : { fen: value.fen }),
    ...(value.maxPlies === undefined ? {} : { maxPlies: value.maxPlies }),
    ...(value.draftId === undefined ? {} : { draftId: value.draftId }),
    ...(value.exportId === undefined ? {} : { exportId: value.exportId }),
  };
};

const activityKinds: readonly NikolaActivityKind[] = [
  "coach_thinking",
  "commentary",
  "reasoning_summary",
  "player_data_checked",
  "games_reviewed",
  "position_checked",
  "line_compared",
  "patterns_checked",
  "development_checked",
  "guide_checked",
  "analysis_started",
  "memory_searched",
  "web_searched",
  "source_read",
  "sources_cross_checked",
];
const activityIcons: readonly NikolaActivityIcon[] = [
  "sync_games",
  "recent_games",
  "start_analysis",
  "refresh_analysis",
  "game_review",
  "game_search",
  "player_record",
  "player_development",
  "pattern_record",
  "line_evaluation",
  "move_evidence",
  "critical_moments",
  "pattern_examples",
  "candidate_comparison",
  "help_guide",
  "memory_search",
  "web_search",
  "source_read",
  "source_cross_check",
];
const publicActivityLabel = /^[\p{L}\p{N}][\p{L}\p{N} .,'’·:/+!?()&—-]{0,119}$/u;

export const normalizeNikolaActivity = (value: unknown): NikolaActivity | null => {
  if (!isRecord(value)) return null;
  if (
    Object.keys(value).some(
      (key) =>
        !["activityId", "sequence", "kind", "status", "label", "icon", "detail", "occurredAt", "progress"].includes(
          key,
        ),
    )
  )
    return null;
  if (
    value.activityId !== undefined &&
    (typeof value.activityId !== "string" ||
      !/^[a-z][a-z0-9_-]{1,63}$/u.test(value.activityId) ||
      privateActivityId.test(value.activityId))
  )
    return null;
  if (
    value.sequence !== undefined &&
    (typeof value.sequence !== "number" || !Number.isSafeInteger(value.sequence) || value.sequence < 0)
  )
    return null;
  if (!activityKinds.includes(value.kind as NikolaActivityKind)) return null;
  if (value.status !== "running" && value.status !== "complete" && value.status !== "failed") return null;
  if (
    typeof value.label !== "string" ||
    !publicActivityLabel.test(value.label) ||
    privateActivityText.test(value.label)
  )
    return null;
  if (
    !optionalString(value.detail) ||
    (value.detail && (value.detail.length > 240 || privateActivityText.test(value.detail)))
  )
    return null;
  if (value.occurredAt !== undefined && !boundedDate(value.occurredAt)) return null;
  if (value.icon !== undefined && !activityIcons.includes(value.icon as NikolaActivityIcon)) return null;
  if (
    value.progress !== undefined &&
    (typeof value.progress !== "number" ||
      !Number.isFinite(value.progress) ||
      value.progress < 0 ||
      value.progress > 100)
  )
    return null;
  return {
    ...(value.activityId === undefined ? {} : { activityId: value.activityId }),
    ...(value.sequence === undefined ? {} : { sequence: value.sequence }),
    kind: value.kind as NikolaActivityKind,
    status: value.status,
    label: value.label,
    ...(value.icon === undefined ? {} : { icon: value.icon as NikolaActivityIcon }),
    ...(value.detail === undefined ? {} : { detail: value.detail }),
    ...(value.occurredAt === undefined ? {} : { occurredAt: value.occurredAt }),
    ...(value.progress === undefined ? {} : { progress: value.progress }),
  };
};

const normalizeNikolaMessagePart = (value: unknown): NikolaMessagePart | null => {
  if (!isRecord(value)) return null;

  if (value.type === "text")
    return typeof value.markdown === "string" ? { type: "text", markdown: value.markdown } : null;
  if (value.type === "activity") {
    const activity = normalizeNikolaActivity(value.activity);
    return activity ? { type: "activity", activity } : null;
  }
  if (value.type === "sources") {
    if (!hasOnlyKeys(value, ["type", "sources"]) || !Array.isArray(value.sources) || value.sources.length === 0)
      return null;
    const sources = value.sources.slice(0, MAX_NIKOLA_SOURCES).map(normalizeNikolaSourceCitation);
    if (sources.includes(null)) return null;
    return { type: "sources", sources: sources as NikolaSourceCitation[] };
  }
  if (value.type === "evidence") {
    const evidence = value.evidence;
    const reference =
      isRecord(evidence) && evidence.reference !== undefined
        ? normalizeNikolaGameReference(evidence.reference)
        : undefined;
    if (
      !isRecord(evidence) ||
      typeof evidence.id !== "string" ||
      typeof evidence.title !== "string" ||
      typeof evidence.summary !== "string" ||
      !isStringArray(evidence.supportingFactIds) ||
      !optionalString(evidence.gameId) ||
      !optionalString(evidence.fen) ||
      (evidence.ply !== undefined && typeof evidence.ply !== "number") ||
      reference === null
    )
      return null;
    return {
      type: "evidence",
      evidence: {
        id: evidence.id,
        title: evidence.title,
        summary: evidence.summary,
        supportingFactIds: evidence.supportingFactIds,
        ...(evidence.gameId === undefined ? {} : { gameId: evidence.gameId }),
        ...(evidence.ply === undefined ? {} : { ply: evidence.ply }),
        ...(evidence.fen === undefined ? {} : { fen: evidence.fen }),
        ...(reference === undefined ? {} : { reference }),
      },
    };
  }
  if (value.type === "actions") {
    if (!Array.isArray(value.actions) || !isDenseArray(value.actions)) return null;
    const actions = value.actions.map(normalizeNikolaAction);
    return actions.every((action): action is NikolaAction => action !== null) ? { type: "actions", actions } : null;
  }
  if (value.type === "board")
    return typeof value.fen === "string" &&
      (value.orientation === "w" || value.orientation === "b") &&
      isStringArray(value.supportingFactIds)
      ? { type: "board", fen: value.fen, orientation: value.orientation, supportingFactIds: value.supportingFactIds }
      : null;
  if (value.type === "coaching") {
    if (!hasOnlyKeys(value, ["type", "coaching"])) return null;
    const coaching = normalizeNikolaCoachingAnswer(value.coaching);
    return coaching ? { type: "coaching", coaching } : null;
  }
  return null;
};

export const normalizeNikolaMessageParts = (value: unknown, fallbackText: string): readonly NikolaMessagePart[] => {
  const fallback = (): readonly NikolaMessagePart[] => [{ type: "text", markdown: fallbackText }];
  if (!Array.isArray(value) || !isDenseArray(value) || value.length === 0 || value.length > MAX_NIKOLA_MESSAGE_PARTS)
    return fallback();
  try {
    if (utf8ByteLength(JSON.stringify(value)) > MAX_NIKOLA_MESSAGE_METADATA_BYTES) return fallback();
  } catch {
    return fallback();
  }
  const parts = value.map(normalizeNikolaMessagePart);
  return parts.every((part): part is NikolaMessagePart => part !== null) ? parts : fallback();
};

export interface ReviewCandidate {
  san: string;
  evaluation: EvaluationScore;
  source: "played" | "engine";
}

export interface VerifiedAlternative {
  moves: readonly string[];
  finalEvaluation: EvaluationScore | null;
}

export const REVIEW_SELECTION_REASONS = [
  "player_marked",
  "consequential_error",
  "missed_opportunity",
  "quiet_evidence",
  "reusable_strength",
] as const;
export type ReviewSelectionReason = (typeof REVIEW_SELECTION_REASONS)[number];

export const REVIEW_DECISION_PROBLEMS = [
  "missed_tactic",
  "allowed_opponent_threat",
  "created_tactic",
  "rushed_decision",
  "time_pressure_decision",
  "conversion",
  "evaluation_mismatch",
] as const;
export type ReviewDecisionProblem = (typeof REVIEW_DECISION_PROBLEMS)[number];

export const MAX_REVIEW_RECURRENCE_REFERENCES = 3;

export interface ReviewRecurrenceReference {
  factId: string;
  gameId: string;
  ply: number;
  fenBefore: string;
  playedSan: string;
  centipawnLoss: number;
  playedAt: string;
  classifierVersion: string;
}

export interface ReviewRecurrenceEvidence {
  motif: string;
  format: TimeControl;
  evidenceWindow: { maxGames: number; maxAgeDays: number };
  references: readonly ReviewRecurrenceReference[];
}

export interface CriticalMoment {
  factId: string;
  ply: number;
  phase: "opening" | "middlegame" | "endgame";
  kind: CriticalMomentKind;
  fenBefore: string;
  playedSan: string;
  candidates: readonly ReviewCandidate[];
  playerEvalBefore: EvaluationScore;
  playerEvalAfter: EvaluationScore;
  clockSeconds: number | null;
  timeSpentSeconds: number | null;
  motifs: readonly string[];
  patternIds: readonly string[];
  decisionQuestion: string;
  consequence: string;
  verifiedAlternative?: VerifiedAlternative;
  selectionReason: ReviewSelectionReason;
  decisionProblem: ReviewDecisionProblem | null;
  futureCue: string;
  recurrence: ReviewRecurrenceEvidence | null;
  /** @deprecated Use futureCue. Retained for existing clients during v1 rollout. */
  cue: string;
  reflection?: string | null;
}

export interface ReviewMomentReference {
  gameId: string;
  factId: string;
}

export interface CriticalMomentsReview {
  gameId: string;
  format: TimeControl;
  playerColor: ChessColor;
  opening: string | null;
  result: string;
  moments: readonly CriticalMoment[];
  deterministicSummary: string;
}
